import { useEffect, useState } from 'react'
import { Box, Flex } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  IconChevronDown,
  IconChevronUp,
  IconDots,
  IconSearch,
} from '@tabler/icons-react'
import { practiceTestService } from '@/services/practiceTestService'
import {
  CreatorType,
  formatPracticeTestStatusTypeLabel,
  PracticeTestListResponse,
  PracticeTestStatus,
  PracticeTestType,
} from '@/types/practice-test'
import copy from 'copy-to-clipboard'
import { useParticipantStore } from '@/stores/participantStore'
import { cn } from '@/lib/utils'
import { STUDENT_SITE_URL } from '@/utils/constants'
import {
  getCreatorLabel,
  getMarksPercentage,
  getMarksStyle,
} from '@/utils/style'
import { FormatFullDateMonthYearHourMinute } from '@/utils/time'
import { useDebounce } from '@/hooks/useDebounce'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import Pagination from '@/components/ui/pagination'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import ArrangeTestModal from '@/features/test/components/ArrangeTestModal'
import { GroupedByHeadingTable } from './GroupedByHeadingTable'

function formatCreatorType(type?: string | null) {
  if (!type) return 'N/A'
  if (type === CreatorType.COMMON) return 'Admin created'
  return type.replace(/_/g, ' ')
}

const PracticeTestTable = ({
  classId,
  moduleId,
}: {
  classId: string
  moduleId: string
}) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [creatorType, setCreatorType] = useState<CreatorType | null>(null)
  const [status, setStatus] = useState<PracticeTestStatus | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isArrangeOpen, setIsArrangeOpen] = useState(false)
  const [isHeadingEnabled, setIsHeadingEnabled] = useState(true)
  const debouncedQuery = useDebounce(search) // wait 500ms

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const { data: practiceTests } = useQuery({
    queryKey: [
      'practiceTests',
      classId,
      page,
      debouncedQuery,
      creatorType,
      status,
    ],
    queryFn: () =>
      practiceTestService.getPracticeTestList({
        class_id: Number(classId),
        page,
        limit: 10,
        search: debouncedQuery.trim(),
        is_practice: true,
        creator_type: creatorType,
        status,
      }),
  })

  return (
    <Card className='right-0 left-0 z-100 py-1'>
      <CardContent className='p-0'>
        <div className={'space-y-4 rounded-lg bg-[#FFFFFF] p-4'}>
          <div className='flex items-center justify-between'>
            <p className='text-2xl font-[400] text-[#002652]'>Practice Tests</p>
            <Button onClick={() => setIsArrangeOpen(true)}>Arrange Test</Button>
          </div>
          <Flex align={'center'} justify={'between'} className='max-h-10'>
            <Flex className='rounded-[10px] bg-[#F6F7F9] p-1' gap={'1'}>
              {(
                [
                  { value: null, label: 'All' },
                  {
                    value: PracticeTestStatus.PUBLISHED,
                    label: PracticeTestStatus.PUBLISHED.replace(/_/g, ' '),
                  },
                  { value: PracticeTestStatus.INACTIVE, label: 'deactivated' },
                ] as const
              ).map(({ value, label }) => (
                <div
                  key={value ?? 'all'}
                  className={cn(
                    'cursor-pointer rounded-[6px] px-3 py-1.5 text-sm capitalize',
                    status === value
                      ? 'bg-white text-[#002652] shadow-[0_1px_2px_#023A7C1A]'
                      : 'bg-transparent text-[#667085]'
                  )}
                  onClick={() => {
                    setStatus(value)
                    setPage(1)
                  }}
                >
                  {label}
                </div>
              ))}
            </Flex>

            <div className='relative'>
              <IconSearch className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' />
              <Input
                placeholder={'Search'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className='h-10 w-[150px] pl-9 lg:w-[250px]'
              />
            </div>

            <Box>
              <DropdownMenu onOpenChange={setIsDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='outline'
                    className='inline-flex h-8 w-[180px] items-center justify-between gap-1 px-3 lg:px-4'
                  >
                    <span className='truncate capitalize'>
                      Type:{' '}
                      {creatorType ? formatCreatorType(creatorType) : 'All'}
                    </span>
                    {isDropdownOpen ? (
                      <IconChevronUp className='size-4 flex-shrink-0' />
                    ) : (
                      <IconChevronDown className='size-4 flex-shrink-0' />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    key='all'
                    className={cn(
                      'cursor-pointer px-3 py-2 text-sm',
                      creatorType === null
                        ? 'bg-gray-100 text-[#002652]'
                        : 'text-[#475467] hover:bg-gray-100'
                    )}
                    onSelect={() => {
                      setCreatorType(null)
                      setPage(1)
                    }}
                  >
                    All
                  </DropdownMenuItem>
                  {Object.values(CreatorType).map((item: string) => (
                    <DropdownMenuItem
                      key={item}
                      className={cn(
                        'cursor-pointer px-3 py-2 text-sm capitalize',
                        creatorType === item
                          ? 'bg-gray-100 text-[#002652]'
                          : 'text-[#475467] hover:bg-gray-100'
                      )}
                      onSelect={() => {
                        setCreatorType(item as CreatorType)
                        setPage(1)
                      }}
                    >
                      {formatCreatorType(item)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </Box>
            <Flex align={'center'} justify={'center'} gap={'2'}>
              <label
                htmlFor='practice-tests-heading-toggle'
                className='text-sm font-[400] text-[#002652]'
              >
                Heading
              </label>
              <Switch
                id='practice-tests-heading-toggle'
                checked={isHeadingEnabled}
                onCheckedChange={setIsHeadingEnabled}
              />
            </Flex>
          </Flex>
        </div>
        {/* {status === PracticeTestStatus.DRAFT && (
          <DraftTable
            data={practiceTests}
            moduleId={moduleId}
            classId={classId}
          />
        )} */}
        {isHeadingEnabled ? (
          <GroupedByHeadingTable
            status={status}
            isHeadingEnabled={isHeadingEnabled}
            moduleId={moduleId}
            classId={classId}
            search={debouncedQuery}
            creatorType={creatorType}
            showStatusColumn={status === null}
            isPractice
          />
        ) : (
          <>
            {(status === null || status === PracticeTestStatus.PUBLISHED) && (
              <PublishedTable
                data={practiceTests}
                moduleId={moduleId}
                classId={classId}
                showStatusColumn={status === null}
              />
            )}
            {status === PracticeTestStatus.INACTIVE && (
              <DeactivatedTable
                data={practiceTests}
                moduleId={moduleId}
                classId={classId}
              />
            )}
          </>
        )}

        {!isHeadingEnabled && (
          <div className='mt-[15px] px-[24px] py-[16px]'>
            <Pagination
              justify={'end'}
              currentPage={page}
              onPageChange={handlePageChange}
              totalPages={practiceTests?.meta.total_pages || 1}
            />
          </div>
        )}

        <ArrangeTestModal
          open={isArrangeOpen}
          onOpenChange={setIsArrangeOpen}
          classId={classId}
          creatorType={creatorType}
          status={status}
          isPractice
        />
      </CardContent>
    </Card>
  )
}

export default PracticeTestTable

// function DraftTable({
//   data,
//   moduleId,
// }: {
//   data?: PracticeTestListResponse
//   moduleId: string
//   classId: string
// }) {
//   const navigate = useNavigate()

//   return (
//     <Table className='w-full table-fixed'>
//       <colgroup>
//         <col className='w-6/7' />
//         <col className='w-2/7' />
//         <col className='w-1/8' />
//         <col className='w-1/12' />
//         <col className='w-1/12' />
//         <col className='w-1/7' />
//       </colgroup>
//       <TableHeader>
//         <TableRow className='bg-[#F2F4F7]'>
//           <TableHead className='font-mundial px-[24px] py-[12px] text-sm font-[600] text-[#002652]'>
//             Name
//           </TableHead>
//           <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
//             Updated at
//           </TableHead>
//           <TableHead className='font-mundial w-fit text-left text-sm font-[600] text-[#002652]'>
//             Question
//           </TableHead>
//           <TableHead className='font-mundial text-left text-sm font-[600] text-[#002652]'>
//             Time
//           </TableHead>
//           <TableHead className='font-mundial text-left text-sm font-[600] text-[#002652]'>
//             Marks
//           </TableHead>
//           <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
//         </TableRow>
//       </TableHeader>
//       <TableBody className='[&_tr:last-child]:border-b-1'>
//         {!data ||
//           (data?.data?.length === 0 && (
//             <TableRow>
//               <TableCell colSpan={8} className='h-24 text-center'>
//                 No data found.
//               </TableCell>
//             </TableRow>
//           ))}
//         {data?.data?.map((item) => (
//           <TableRow key={item.id} className='hover:bg-gray-50'>
//             <TableCell className='px-[24px] py-[12px]'>
//               <div>
//                 <div className='text-sm font-[400] text-[#002652]'>
//                   {item.name}
//                 </div>
//                 <div className='truncate text-sm font-[300] text-[#475467]'>
//                   Created date:{' '}
//                   {FormatFullDateMonthYearHourMinute(item.created_at)}
//                 </div>
//               </div>
//             </TableCell>
//             <TableCell className='text-center text-sm font-[300] text-[#002652]'>
//               {FormatFullDateTimeWithAMPM(item.updated_at) || 'N/A'}
//             </TableCell>
//             <TableCell
//               className={cn(
//                 'truncate text-start text-sm font-[300] text-[#002652]'
//               )}
//             >
//               {'N/A'}
//             </TableCell>
//             <TableCell
//               className={cn(
//                 'truncate text-start text-sm font-[300] text-[#002652]'
//               )}
//             >
//               {'N/A'}
//             </TableCell>
//             <TableCell
//               className={cn(
//                 'truncate text-start text-sm font-[300] text-[#002652]'
//               )}
//             >
//               {'N/A'}
//             </TableCell>
//             <TableCell className='w-[5%] px-3 text-center'>
//               <Button
//                 variant='link'
//                 className='h-auto p-0 px-6 font-medium text-[#0057B8] hover:text-blue-800'
//                 onClick={() => {
//                   navigate({
//                     to: '/create-mock-exam/detail/$ptId/$moduleId',
//                     params: { ptId: item.id.toString(), moduleId },
//                   })
//                 }}
//               >
//                 View detail
//               </Button>
//             </TableCell>
//           </TableRow>
//         ))}
//       </TableBody>
//     </Table>
//   )
// }
function PublishedTable({
  data,
  moduleId,
  classId,
  showStatusColumn = false,
}: {
  data?: PracticeTestListResponse
  moduleId: string
  classId: string
  showStatusColumn?: boolean
}) {
  const navigate = useNavigate()
  const { updateExamTestData } = useParticipantStore()
  const colCount = 7 + (showStatusColumn ? 1 : 0)

  return (
    <Table className='w-full table-fixed'>
      <colgroup>
        <col className='w-4/7' />
        <col className='w-1/7' />
        <col className='w-1/7' />
        <col className='w-2/7' />
        <col className='w-2/7' />
        <col className='w-2/7' />
        {showStatusColumn ? <col className='w-1/7' /> : null}
        <col className='w-1/7' />
      </colgroup>
      <TableHeader>
        <TableRow className='bg-[#F2F4F7]'>
          <TableHead className='font-mundial px-[24px] py-[12px] text-sm font-[600] text-[#002652]'>
            Name
          </TableHead>
          <TableHead className='font-mundial text- text-center text-sm font-[600] text-[#002652]'>
            Paticipants
          </TableHead>
          <TableHead className='font-mundial text- w-fit text-center text-sm font-[600] text-[#002652]'>
            Unmarked
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average AI Mark
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average Tutor Mark
          </TableHead>
          <TableHead className='font-mundial text-sm font-[600] text-[#002652]'>
            Type
          </TableHead>
          {showStatusColumn ? (
            <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
              Status
            </TableHead>
          ) : null}
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
        </TableRow>
      </TableHeader>
      <TableBody className='[&_tr:last-child]:border-b-1'>
        {!data ||
          (data?.data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} className='h-24 text-center'>
                No data found.
              </TableCell>
            </TableRow>
          ))}
        {data?.data?.map((item) => (
          <TableRow key={item.id} className='hover:bg-gray-50'>
            <TableCell className='px-[24px] py-[12px]'>
              <div>
                <div className='truncate text-sm font-[400] text-[#002652]'>
                  {item.name}
                </div>
                <div className='truncate text-sm font-[300] text-[#475467]'>
                  Created date:{' '}
                  {FormatFullDateMonthYearHourMinute(item.created_at)}
                </div>
              </div>
            </TableCell>
            <TableCell className='text-center text-sm font-[300] text-[#002652]'>
              {item.participants ?? 'N/A'}
            </TableCell>
            <TableCell className='text-center text-sm font-[300] text-[#002652]'>
              {item.unmarked ?? 'N/A'}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.avg_ai_marks)
              )}
            >
              {getMarksPercentage(item.avg_ai_marks)}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.avg_tutor_marks)
              )}
            >
              {getMarksPercentage(item.avg_tutor_marks)}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-sm font-[300] text-[#002652] capitalize'
              )}
            >
              {getCreatorLabel(item.creator_type)}
            </TableCell>
            {showStatusColumn ? (
              <TableCell className='text-center text-sm font-[300] capitalize text-[#002652]'>
                {formatPracticeTestStatusTypeLabel(item.status_type)}
              </TableCell>
            ) : null}
            <TableCell className='w-[5%] px-3 text-center'>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='link'
                    className='h-auto rounded px-2 py-1 font-medium text-[#0057B8] hover:bg-[#dbe9fa]'
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <IconDots />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='end'
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {item.creator_type.toLocaleLowerCase() !==
                    PracticeTestType.STUDENT && (
                    <DropdownMenuItem
                      onClick={() => {
                        navigate({
                          to: '/create-practice-test/detail/$ptId/$moduleId',
                          params: { ptId: item.id.toString(), moduleId },
                          search: { source: 'classDetailPracticeTest' },
                        })
                      }}
                    >
                      View details
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => {
                      updateExamTestData(item)
                      navigate({
                        to: '/marking/list-participant/$ptId/$classId',
                        params: { ptId: item.id.toString(), classId },
                        search: { source: 'classDetailPracticeTest' },
                      })
                    }}
                  >
                    {item.creator_type.toLocaleLowerCase() !==
                    PracticeTestType.STUDENT
                      ? 'View participants'
                      : 'View'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      copy(
                        `${STUDENT_SITE_URL}/my-class/detail/${classId}?test_id=${item.id}`
                      )
                    }}
                  >
                    Copy test link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
function DeactivatedTable({
  data,
  moduleId,
  classId,
}: {
  data?: PracticeTestListResponse
  moduleId: string
  classId: string
}) {
  const navigate = useNavigate()
  const { updateExamTestData } = useParticipantStore()

  return (
    <Table className='w-full table-fixed'>
      <colgroup>
        <col className='w-4/7' />
        <col className='w-1/7' />
        <col className='w-1/7' />
        <col className='w-2/7' />
        <col className='w-2/7' />
        <col className='w-2/7' />
        <col className='w-1/7' />
      </colgroup>
      <TableHeader>
        <TableRow className='bg-[#F2F4F7]'>
          <TableHead className='font-mundial px-[24px] py-[12px] text-sm font-[600] text-[#002652]'>
            Name
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Paticipants
          </TableHead>
          <TableHead className='font-mundial w-fit text-center text-sm font-[600] text-[#002652]'>
            Unmarked
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average AI Mark
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average Tutor Mark
          </TableHead>
          <TableHead className='font-mundial text-sm font-[600] text-[#002652]'>
            Type
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
        </TableRow>
      </TableHeader>
      <TableBody className='[&_tr:last-child]:border-b-1'>
        {!data ||
          (data?.data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className='h-24 text-center'>
                No data found.
              </TableCell>
            </TableRow>
          ))}
        {data?.data?.map((item) => (
          <TableRow key={item.id} className='hover:bg-gray-50'>
            <TableCell className='px-[24px] py-[12px]'>
              <div>
                <div className='truncate text-sm font-[400] text-[#002652]'>
                  {item.name}
                </div>
                <div className='truncate text-sm font-[300] text-[#475467]'>
                  Created date:{' '}
                  {FormatFullDateMonthYearHourMinute(item.created_at)}
                </div>
              </div>
            </TableCell>
            <TableCell className='text-center text-sm font-[300] text-[#002652]'>
              {item.participants ?? 'N/A'}
            </TableCell>
            <TableCell className='text-center text-sm font-[300] text-[#002652]'>
              {item.unmarked ?? 'N/A'}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.avg_ai_marks)
              )}
            >
              {getMarksPercentage(item.avg_ai_marks)}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.avg_tutor_marks)
              )}
            >
              {getMarksPercentage(item.avg_tutor_marks)}
            </TableCell>
            <TableCell
              className={cn('text-sm font-[300] text-[#002652] capitalize')}
            >
              {getCreatorLabel(item.creator_type)}
            </TableCell>
            <TableCell className='w-[5%] px-3 text-center'>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='link'
                    className='h-auto rounded px-2 py-1 font-medium text-[#0057B8] hover:bg-[#dbe9fa]'
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <IconDots />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='end'
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  {item.creator_type.toLocaleLowerCase() !==
                    PracticeTestType.STUDENT && (
                    <DropdownMenuItem
                      onClick={() => {
                        navigate({
                          to: '/create-practice-test/detail/$ptId/$moduleId',
                          params: { ptId: item.id.toString(), moduleId },
                          search: { source: 'classDetailPracticeTest' },
                        })
                      }}
                    >
                      View details
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => {
                      updateExamTestData(item)
                      navigate({
                        to: '/marking/list-participant/$ptId/$classId',
                        params: { ptId: item.id.toString(), classId },
                        search: { source: 'classDetailPracticeTest' },
                      })
                    }}
                  >
                    {item.creator_type.toLocaleLowerCase() !==
                    PracticeTestType.STUDENT
                      ? 'View participants'
                      : 'View'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
